import {
  ComponentType,
  FC,
  memo,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { OverlayContainer, FlatOverlayContainer } from "./OverlayContainer";
import { LANGUAGE, SIDE } from "_constants";
import { MatchStatus } from "../service";
import { getSide } from "utils/get-side";
import { getNormalizedStatusCode } from "utils/get-normalized-status-code";
import ResizeObserver from "resize-observer-polyfill";

export type StatusRendererProps = {
  homeTeam: string;
  awayTeam: string;
  activeTeam?: string | undefined;
  side: SIDE;
  code?: string;
  normalizedCode?: string;
  message?: string;
  status: MatchStatus;
  language: LANGUAGE;
  description?: string;
  Icon?: ComponentType<any>;
  width: number;
  height: number;
};

type GeneratorProps = {
  language: LANGUAGE;
  normalizedStatusCode?: string;
  statusCode?: string;
  statusMessage?: string;
};

type OverlayProps = {
  className?: string;
  status: MatchStatus;
  StatusRenderer?: ComponentType<StatusRendererProps>;
  language?: LANGUAGE;
  children: ReactNode;
  messageGenerator: (
    props: GeneratorProps
  ) =>
    | { message: string; description?: string; icon?: ComponentType<any> }
    | undefined;
  flat?: boolean;
};

export const Overlay: FC<OverlayProps> = memo(
  ({
    className = "",
    StatusRenderer,
    status,
    language = LANGUAGE.ENGLISH,
    messageGenerator,
    children,
    flat = false,
  }) => {
    const side = getSide(status);
    const normalizedStatusCode = getNormalizedStatusCode(status);
    const homeTeamName =
      (status as any)?.T1?.n ??
      (status as any)?.t1?.n ??
      (status as any)?.T1n ??
      (status as any)?.t1n ??
      "";

    const awayTeamName =
      (status as any)?.T2?.n ??
      (status as any)?.t2?.n ??
      (status as any)?.T2n ??
      (status as any)?.t2n ??
      "";

    const { message, description, icon } =
      messageGenerator({
        language: language,
        normalizedStatusCode: normalizedStatusCode,
        statusCode: status.sc,
        statusMessage: status.stn,
      }) || {};

    const activeTeam =
      side === SIDE.NO_SIDE
        ? undefined
        : (side === SIDE.HOME ? homeTeamName : awayTeamName) || undefined;

    const [{ width, height }, setState] = useState({ width: 0, height: 0 });
    const overlayRef = useRef<HTMLDivElement>(null);
    const messageContainerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
      setState((state) => ({
        ...state,
        height: overlayRef.current ? overlayRef.current.clientHeight : 0,
        width: overlayRef.current ? overlayRef.current.clientWidth : 0,
      }));
      const ro = new ResizeObserver((entries: any, observer: any) => {
        for (var { contentRect } of entries) {
          const { width, height } = contentRect;
          setState((state) => ({
            ...state,
            width,
            height,
          }));
        }
      });

      overlayRef.current && ro.observe(overlayRef.current);

      return () => {
        ro.disconnect();
      };
    }, []);

    const Container = flat ? FlatOverlayContainer : OverlayContainer;

    return (
      <>
        <Container ref={overlayRef} className="overlay-content-wrapper">
          <div
            ref={messageContainerRef}
            className={[
              "status-message-container flex h-full w-full",
              `sc-${status.sc}`,
              `nsc-${normalizedStatusCode}`,
              `side-${side}`,
              className,
            ]
              .filter(Boolean)
              .join(" ")}
            data-side={side}
          >
            {children}
          </div>
        </Container>
        <div
          className="inner-container mt-[15%] flex h-1/2 w-full items-center justify-center px-[1vw]"
          data-side={side}
        >
          {StatusRenderer && (
            <StatusRenderer
              side={side}
              homeTeam={homeTeamName}
              awayTeam={awayTeamName}
              activeTeam={activeTeam}
              code={status.sc}
              message={message}
              normalizedCode={normalizedStatusCode}
              status={status}
              language={language}
              description={description}
              Icon={icon}
              width={width}
              height={height}
            />
          )}
        </div>
      </>
    );
  }
);
